import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useGql } from '@ah/design';
import { Header } from '@ah/design/mobile';
import { MUSIC_ARTIST_DETAIL_QUERY } from '../shared/queries.ts';
import type { MusicArtist } from '../shared/types.ts';
import { SongRow, SongListSkeleton, ErrorRetry } from './_parts.tsx';

export function ArtistDetail() {
  const { slug } = useParams<{ slug: string }>();
  const gql = useGql();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['music', 'artist', slug],
    queryFn: async () => {
      if (!slug) return null;
      const res = await gql.request<{ musicArtist: MusicArtist | null }>(MUSIC_ARTIST_DETAIL_QUERY, { slug });
      return res.musicArtist;
    },
    enabled: !!slug,
  });

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title="Music" backTo="/music/artists" />
      <main className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {isLoading ? (
          <SongListSkeleton />
        ) : error || !data ? (
          <ErrorRetry label="Could not load this artist." onRetry={() => refetch()} />
        ) : (
          <>
            <div className="space-y-0.5">
              <p className="text-lg font-semibold leading-snug">{data.name}</p>
              <p className="text-xs text-muted-foreground">{data.tagline}</p>
              <p className="text-[11px] text-muted-foreground/70">{data.style}</p>
            </div>
            {data.bio && (
              <p className="text-[13px] text-foreground/85 leading-relaxed">{data.bio}</p>
            )}
            <div className="pt-2">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                Songs
              </p>
              {(data.latestSongs ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  No songs yet — first one is on the way.
                </p>
              ) : (
                <div className="space-y-3">
                  {(data.latestSongs ?? []).map((s) => <SongRow key={s.id} song={s} />)}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
